import React from 'react';
import { Building2, Database, Users, TrendingUp, FileText, Settings, MessageCircle, Plus } from 'lucide-react';
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Heading, 
  Grid, 
  GridItem,
  Button,
  Badge,
  Card,
  CardBody,
  Avatar,
  Flex,
  Divider,
  useColorModeValue
} from '@chakra-ui/react';
import type { ChatThread } from '../App';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
}

const integrations: Integration[] = [
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'CRM система для управления продажами',
    icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
    category: 'CRM'
  },
  {
    id: 'sap',
    name: 'SAP ERP',
    description: 'Система планирования ресурсов предприятия',
    icon: <Database className="w-8 h-8 text-orange-600" />,
    category: 'ERP'
  },
  {
    id: 'workday',
    name: 'Workday',
    description: 'HR система управления персоналом',
    icon: <Users className="w-8 h-8 text-green-600" />,
    category: 'HR'
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Маркетинг и CRM платформа',
    icon: <Building2 className="w-8 h-8 text-purple-600" />,
    category: 'CRM'
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Система управления проектами',
    icon: <FileText className="w-8 h-8 text-indigo-600" />,
    category: 'Project Management'
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    description: 'Платформа автоматизации ИТ-процессов',
    icon: <Settings className="w-8 h-8 text-red-600" />,
    category: 'ITSM'
  }
];

interface IntegrationsPageProps {
  onIntegrationSelect: (integration: string) => void;
  chatThreads: ChatThread[];
  onThreadSelect: (threadId: string) => void;
}

export function IntegrationsPage({ onIntegrationSelect, chatThreads, onThreadSelect }: IntegrationsPageProps) {
  const bgGradient = useColorModeValue(
    'linear(to-b, blue.50, white)', 
    'linear(to-b, gray.900, gray.800)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const integrationNames: Record<string, string> = {
    salesforce: 'Salesforce',
    sap: 'SAP ERP',
    workday: 'Workday',
    hubspot: 'HubSpot',
    jira: 'Jira',
    servicenow: 'ServiceNow'
  };

  const positionNames: Record<string, string> = {
    'sales-manager': 'Менеджер по продажам',
    'sales-admin': 'Администратор Salesforce',
    'sap-consultant': 'SAP Консультант',
    'business-analyst': 'Бизнес-аналитик',
    'hr-manager': 'HR Менеджер',
    'payroll-specialist': 'Специалист по зарплате',
    'marketing-manager': 'Маркетинг менеджер',
    'project-manager': 'Проект менеджер',
    'it-admin': 'ИТ Администратор'
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 3600000) { // Меньше часа
      return `${Math.floor(diff / 60000)} мин назад`;
    } else if (diff < 86400000) { // Меньше дня
      return `${Math.floor(diff / 3600000)} ч назад`;
    } else {
      return `${Math.floor(diff / 86400000)} дн назад`;
    }
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Box maxW="md" mx="auto" p={4} pb={20}>
        {/* Header */}
        <VStack spacing={4} textAlign="center" mb={8} pt={8}>
          <Avatar
            size="lg"
            bg="blue.500"
            icon={<Building2 size={32} color="white" />}
          />
          <VStack spacing={2}>
            <Heading size="lg" color={textColor}>ИИ Помощник</Heading>
            <Text color={mutedColor}>Выберите интеграцию или продолжите чат</Text>
          </VStack>
        </VStack>

        {/* Recent Chats */}
        {chatThreads.length > 0 && (
          <VStack spacing={4} mb={8}>
            <HStack justify="space-between" w="full">
              <Heading size="md" color={textColor}>Недавние чаты</Heading>
              <Button variant="ghost" size="sm" leftIcon={<MessageCircle size={16} />}>
                Все
              </Button>
            </HStack>
            
            <VStack spacing={3} w="full">
              {chatThreads.slice(0, 3).map((thread) => (
                <Card 
                  key={thread.id}
                  bg={cardBg}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ 
                    shadow: "md",
                    borderColor: "blue.200"
                  }}
                  borderWidth={2}
                  borderColor="transparent"
                  onClick={() => onThreadSelect(thread.id)}
                  w="full"
                >
                  <CardBody p={4}>
                    <HStack align="start" spacing={3}>
                      <Avatar
                        size="sm"
                        bg="linear-gradient(135deg, #10b981, #2563eb)"
                        icon={<MessageCircle size={16} color="white" />}
                      />
                      <VStack align="start" flex={1} spacing={1}>
                        <HStack justify="space-between" w="full" align="start">
                          <Text fontWeight="medium" color={textColor} wordBreak="break-word" noOfLines={2} flex={1} mr={2}>
                            {thread.title}
                          </Text>
                          <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap" flexShrink={0}>
                            {formatTime(thread.timestamp)}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color={mutedColor} mb={2} wordBreak="break-word" whiteSpace="normal">
                          {positionNames[thread.position]} • {integrationNames[thread.integration]}
                        </Text>
                        <Text fontSize="sm" color={mutedColor} wordBreak="break-word" noOfLines={2}>
                          {thread.lastMessage}
                        </Text>
                        <Badge colorScheme="gray" fontSize="xs" mt={2}>
                          {thread.messageCount} сообщений
                        </Badge>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
            
            <Divider my={6} />
          </VStack>
        )}

        {/* New Chat Section */}
        <VStack spacing={4} mb={6}>
          <HStack spacing={2}>
            <Plus size={20} />
            <Heading size="md" color={textColor}>Новый чат</Heading>
          </HStack>
        </VStack>

        {/* Integrations Grid */}
        <Grid templateColumns="repeat(auto-fit, minmax(140px, 1fr))" gap={4}>
          {integrations.map((integration) => (
            <GridItem key={integration.id}>
              <Card 
                bg={cardBg}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ 
                  shadow: "lg",
                  transform: "scale(1.05)",
                  borderColor: "blue.200"
                }}
                borderWidth={2}
                borderColor="transparent"
                onClick={() => onIntegrationSelect(integration.id)}
                minH="160px"
              >
                <CardBody p={4} display="flex" flexDirection="column" justifyContent="space-between">
                  <VStack spacing={3} textAlign="center" flex={1}>
                    <Flex justify="center">
                      {integration.icon}
                    </Flex>
                    <Heading size="sm" color={textColor} wordBreak="break-word" textAlign="center" lineHeight="1.3">
                      {integration.name}
                    </Heading>
                    <Text fontSize="xs" color={mutedColor} textAlign="center" lineHeight="1.4" wordBreak="break-word" flex={1} display="flex" alignItems="center">
                      {integration.description}
                    </Text>
                    <Badge colorScheme="gray" fontSize="xs" mt="auto">
                      {integration.category}
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          ))}
        </Grid>

        {/* Footer */}
        <Box mt={8} textAlign="center">
          <Text fontSize="sm" color={mutedColor}>
            Более 50+ интеграций доступны
          </Text>
        </Box>
      </Box>
    </Box>
  );
}